/**
 * Recommendations: what the inspector proposes about *our* document.
 *
 * A finding is an external fact — a regulator changed something. A
 * recommendation is a proposal about this document: rename it, give it a
 * cadence or owners, move it to procedures, amend it so a mapped control is
 * actually satisfied. Each carries the concrete change, so Apply performs it
 * rather than leaving a person to translate prose into a form.
 *
 * @module components/regulation/policy/PolicyRecommendations
 */
import { Link, useParams } from "@tanstack/react-router";
import { ArrowRightIcon, CheckCircle2Icon, ChevronRightIcon, LightbulbIcon, RotateCcwIcon, SparklesIcon, XCircleIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";
import { useOrgProfile } from "../../../state/orgProfile";
import {
  useRecommendations,
  type DecisionProofUpload,
  type Recommendation,
  type RecommendationKind,
} from "../../../state/governance";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card } from "../../ui/card";
import { InspectorAvatar } from "../inspector/InspectionStatus";
import { toastManager } from "../../ui/toast";
import { HoverCardTrigger } from "../../ui/hover-card";
import { ComplianceOrganizationMissing, ComplianceUnavailable } from "../ComplianceUnavailable";
import { DecisionBrief } from "../DecisionBrief";
import { firstSentence, suggestionTldr } from "../tldr";
import { ListSkeleton } from "../LoadingSkeletons";
import { formatDate } from "./PolicyGovernanceTabs";
import { RejectDialog } from "./RejectDialog";
import { ReopenDialog } from "./ReopenDialog";
import { focusReopenedAction, reopenTargetSettled, suggestionReopenSummary } from "./ReopenDialog.logic";
import { ResolveRecommendationDialog } from "./ResolveRecommendationDialog";
import { ActionHint } from "./ActionHint";
import { REVIEW_REFERENCE_CLASS_NAME, REVIEW_REFERENCE_TEXT_CLASS_NAME } from "./ReviewReference";
import { ReviewCardLayout } from "./ReviewCardLayout";
import { ReviewSummary } from "./ReviewSummary";
import { findingSeverityTone, FINDING_SEVERITY_LABELS } from "../finding/findingView";
import { SuggestionQueue } from "./SuggestionQueue";
import type { SuggestionDocumentLabel, SuggestionQueueSort } from "./SuggestionQueue.logic";

import { SUGGESTION_STATUS_PILL as STATUS_PILL, KIND_LABELS, proposalLine, groupByDocument, isOpenSuggestion, filterSuggestions, type SuggestionFilters } from "./suggestionsView";
import { librarySuggestionSummary } from "./openWorkByPolicy";

/**
 * The wording a suggestion proposes — the thing a reviewer is actually
 * approving, kept apart from the one-line "where" so it can be read as text.
 *
 * Two kinds carry prose rather than a value: an amendment's replacement
 * wording, and the paragraph a company-profile suggestion would add. For the
 * latter this is the whole of the change, and showing it in full is the
 * safety property — an unattended run reads the organisation's own documents,
 * so the wording it proposes is only ever as trustworthy as the admin's
 * reading of it.
 */
function proposedWording(recommendation: Recommendation): string | null {
  const change = recommendation.proposedChange as Record<string, unknown>;
  const raw = recommendation.kind === "org_context" ? change["contextAddition"] : change["amendment"];
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

/** Kinds whose change the workspace can perform on its own. */
const APPLIES_ITSELF: ReadonlySet<RecommendationKind> = new Set([
  "rename",
  "review_cadence",
  "owner",
  "department",
  "jurisdictions",
  "reclassify",
  "framework",
  "control_mapping",
  // The workspace writes the company profile itself, on the same admin-gated
  // conditional write Settings → Organisation uses. Nothing to upload, no
  // version to name: approving appends the wording.
  "org_context",
]);

/** The supporting file on a decision, served through the workspace, never from storage. */
export function ProofLink({
  organizationId,
  recommendation,
  separator = true,
}: {
  readonly separator?: boolean;
  readonly organizationId: string | null;
  readonly recommendation: Recommendation;
}) {
  const proof = recommendation.decisionProof ?? null;
  if (proof === null || organizationId === null) return null;
  return (
    <>
      {separator ? " · " : null}
      <a
        href={resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/recommendation-proof", {
          organizationId,
          recommendationId: recommendation.id,
        })}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        Proof: {proof.filename}
      </a>
    </>
  );
}

/** Reserve both labels so saving never moves the adjacent decision controls. */
function DecisionLabel({ saving, label }: { saving: boolean; label: string }) {
  return (
    <span className="inline-grid">
      <span aria-hidden={saving} className={cn("col-start-1 row-start-1", saving && "invisible")}>{label}</span>
      <span aria-hidden={!saving} className={cn("col-start-1 row-start-1", !saving && "invisible")}>Saving…</span>
    </span>
  );
}

export function PolicyRecommendations({
  organizationId,
  policyId,
  heading = "Suggestions",
  emptyState,
  everything = false,
  filter,
  bare = false,
  compact = false,
  decisionOnly = false,
  filters,
  queueSort = "priority",
  documentLabels,
}: {
  readonly organizationId: string | null;
  /**
   * A document's recommendations, or — with `null` — the library's own: the
   * ones with no document to sit on, a missing policy above all. Those are
   * fetched org-wide and narrowed here, because the API's filter is an
   * equality on the document and a null cannot be asked for.
   */
  readonly policyId: string | null;
  readonly heading?: string;
  /** What to show when there is nothing — only where the section is a tab of its own. */
  readonly emptyState?: ReactNode;
  /**
   * Every suggestion in the organization. Cards group by document; the compact
   * queue gives each row its document context. `policyId` is ignored.
   */
  readonly everything?: boolean;
  readonly filter?: (item: Recommendation) => boolean;
  /** Just the cards — no heading, no grouping. For a page that is one suggestion. */
  readonly bare?: boolean;
  /** A paged queue linking to individual review pages. */
  readonly compact?: boolean;
  /** The shared decision controls within a suggestion's review page. */
  readonly decisionOnly?: boolean | "header";
  readonly filters?: SuggestionFilters;
  readonly queueSort?: SuggestionQueueSort;
  readonly documentLabels?: ReadonlyMap<string, SuggestionDocumentLabel>;
}) {
  const { snapshot, unavailableReason, refresh, decide } = useRecommendations(
    organizationId,
    everything ? undefined : (policyId ?? undefined),
  );
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });
  const { canManage } = useOrgProfile(organizationId);
  const pendingDecision = useRef(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);
  // Reopening clears a decision of record, so it is confirmed in a dialog.
  const [reopening, setReopening] = useState<string | null>(null);
  // After a reopen, focus moves to the suggestion's next decision once it renders.
  const [focusAfterReopen, setFocusAfterReopen] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [savedDecision, setSavedDecision] = useState<{
    organizationId: string;
    id: string;
    status: Recommendation["status"];
    previousStatus: Recommendation["status"] | undefined;
  } | null>(null);
  useEffect(() => {
    if (savedDecision === null) return;
    if (savedDecision.organizationId !== organizationId) {
      setSavedDecision(null);
      return;
    }
    if (snapshot === null) return;
    const current = snapshot.recommendations.find((item) => item.id === savedDecision.id);
    // Wait for confirmed data, including a colleague's superseding decision.
    if (!current || current.status === savedDecision.status || current.status !== savedDecision.previousStatus) {
      setSavedDecision(null);
    }
  }, [organizationId, savedDecision, snapshot]);

  // Reopened elsewhere (a live refresh, a colleague): drop the pending
  // confirmation, or it reappears by itself when the suggestion is next decided.
  useEffect(() => {
    if (reopenTargetSettled(reopening, snapshot?.recommendations, isOpenSuggestion)) setReopening(null);
  }, [reopening, snapshot]);
  useEffect(() => {
    if (focusAfterReopen !== null && focusReopenedAction(sectionRef.current, focusAfterReopen)) setFocusAfterReopen(null);
  }, [focusAfterReopen, snapshot, busyId, savedDecision]);

  const all = snapshot?.recommendations ?? [];
  const recommendations = useMemo(() => {
    const scoped = all.filter((item) => (everything || item.policyId === policyId) && (filter === undefined || filter(item)));
    const names = documentLabels === undefined ? undefined : new Map([...documentLabels].map(([id, label]) => [id, label.displayName]));
    return filters === undefined ? scoped : filterSuggestions(scoped, filters, names);
  }, [all, everything, policyId, filter, filters, documentLabels]);
  if (organizationId === null) return <ComplianceOrganizationMissing />;
  if (unavailableReason !== null) {
    return <ComplianceUnavailable reason={unavailableReason} onRetry={refresh} />;
  }
  if (snapshot === null) return <ListSkeleton rows={2} />;
  // On the shelf the section is one line, not a stack of cards: how many
  // suggestions wait, how many are missing documents, and the door to the
  // Suggestions page where they are read and decided.
  if (!everything && policyId === null) {
    // "N suggestions" counts what the Suggestions page lists (housekeeping);
    // missing documents are findings and say so on their own.
    const { suggestions: waiting, missingDocuments: missing, profile, documents } = librarySuggestionSummary(all);
    if (waiting === 0 && missing === 0) return null;
    return (
      <Link
        // Missing documents alone live on the Findings page, not Suggestions.
        to={waiting === 0 ? "/$org/compliance/findings" : "/$org/compliance/suggestions"}
        params={{ org: orgSegment }}
        className="mb-3 flex items-center gap-2.5 rounded-xl border border-warning/30 bg-warning/8 px-4 py-2.5 text-sm text-foreground hover:bg-warning/12"
      >
        <LightbulbIcon className="size-4 shrink-0 text-warning" />
        <span className="min-w-0 flex-1">
          {waiting > 0 ? (
            <>
              <span className="font-semibold tabular-nums">{waiting}</span>{" "}
              {waiting === 1 ? "suggestion is" : "suggestions are"} waiting
            </>
          ) : null}
          {missing > 0 ? (
            <span className={waiting > 0 ? "text-muted-foreground" : undefined}>
              {waiting > 0 ? " · " : ""}
              {missing} {missing === 1 ? "document the library is missing" : "documents the library is missing"}
            </span>
          ) : null}
          {profile > 0 ? (
            <span className="text-muted-foreground">
              {" "}· {profile === 1 ? "wording for the company profile" : `${profile} additions to the company profile`}
            </span>
          ) : null}
          {documents > 0 ? (
            <span className="text-muted-foreground">
              {" "}· {documents} {documents === 1 ? "document" : "documents"} with something to fix
            </span>
          ) : null}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
          Read them <ArrowRightIcon className="size-3.5" />
        </span>
      </Link>
    );
  }
  const feedback = <>
    {message !== null ? <p role="alert" className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">{message}</p> : null}
  </>;
  if (recommendations.length === 0) {
    return <div className="space-y-3">{feedback}{emptyState ?? null}</div>;
  }

  const run = async (
    id: string,
    decision: "apply" | "dismiss" | "reopen",
    options?: {
      note?: string;
      resolvedVersion?: number;
      resolvedPolicyId?: string;
      proof?: DecisionProofUpload;
    },
  ): Promise<string | null> => {
    if (pendingDecision.current || savedDecision?.organizationId === organizationId) return "A decision is already being saved. Please wait.";
    pendingDecision.current = true;
    setBusyId(id);
    setMessage(null);
    try {
      const error = await decide(id, decision, {
        note: options?.note ?? null,
        ...(options?.resolvedVersion === undefined ? {} : { resolvedVersion: options.resolvedVersion }),
        ...(options?.resolvedPolicyId === undefined ? {} : { resolvedPolicyId: options.resolvedPolicyId }),
        ...(options?.proof === undefined ? {} : { proof: options.proof }),
      });
      setMessage(error);
      if (error === null) {
        setRejecting(null);
        setResolving(null);
        setReopening(null);
        setSavedDecision({ organizationId, id, previousStatus: all.find((item) => item.id === id)?.status, status: decision === "dismiss" ? "dismissed" : decision === "reopen" ? "open" : "applied" });
        const resolved = decision === "apply" && (options?.resolvedVersion !== undefined || options?.resolvedPolicyId !== undefined);
        toastManager.add({
          type: "success",
          title: decision === "dismiss" ? "Suggestion rejected"
            : decision === "reopen" ? "Suggestion reopened"
            : resolved ? "Suggestion resolved" : "Suggestion applied",
          description: decision === "dismiss" ? "The reason is saved in the decision history."
            : decision === "reopen" ? undefined
            : resolved ? "The evidence is saved with the decision." : "The decision is saved.",
        });
      }
      return error;
    } catch {
      const error = "The decision could not be saved. Check your connection and try again.";
      setMessage(error);
      return error;
    } finally {
      pendingDecision.current = false;
      setBusyId(null);
    }
  };

  const open = recommendations.filter(isOpenSuggestion);
  const decided = recommendations.filter(
    (item) => item.status === "applied" || item.status === "dismissed",
  );

  const isSaving = (id: string) => busyId === id ||
    (savedDecision?.organizationId === organizationId && savedDecision.id === id);

  /** The actions a card carries, on their own — a row and a card share them. */
  const actions = (recommendation: Recommendation) => {
    const header = decisionOnly === "header";
    const saving = isSaving(recommendation.id);
    const busy = busyId !== null || savedDecision?.organizationId === organizationId;
    const canApply = APPLIES_ITSELF.has(recommendation.kind);
    if (!canManage) return null;
    if (isOpenSuggestion(recommendation)) {
      return (
        <>
          {canApply ? (
            <ActionHint
              heading="Approve makes the change now"
              body={
                recommendation.kind === "org_context" ? (
                  <>
                    <p>The wording above is added to the company profile the moment you approve, and the record says who did it and when.</p>
                    <p>Nothing is replaced — it goes after what is already there. Edit or remove it any time in Settings → Organisation.</p>
                  </>
                ) : (
                  <>
                    <p>The {KIND_LABELS[recommendation.kind].toLowerCase()} is set on the document the moment you approve, and the record says who did it and when.</p>
                    <p>Nothing else moves: no version, no upload.</p>
                  </>
                )
              }
            >
              <HoverCardTrigger render={<Button type="button" size="default" disabled={busy} onClick={() => void run(recommendation.id, "apply")} aria-busy={saving} className={header ? "cursor-pointer gap-1.5" : "min-w-24 cursor-pointer gap-1.5"} data-reopen-focus={recommendation.id} />}>
                <CheckCircle2Icon aria-hidden className="size-3.5" /><DecisionLabel saving={saving} label="Approve" />
              </HoverCardTrigger>
            </ActionHint>
          ) : (
              <ActionHint
                heading={recommendation.kind === "missing_document" ? "Resolve the document gap" : "Amend the document"}
                body={recommendation.kind === "missing_document" ? <><p>Upload the document that fills this gap, or pick one uploaded since the gap was raised.</p><p>The suggestion closes against that document, with who resolved it and when. Nobody can close it by clicking alone.</p></> : <><p>Approve Inspector’s proposed PDF, upload the version that carries this amendment, or pick a version uploaded since it was raised.</p><p>The suggestion closes against that version, with who resolved it and when. Nobody can close it by clicking alone.</p></>}
              >
                <HoverCardTrigger render={<Button type="button" size="default" variant="default" disabled={busy} onClick={() => setResolving(recommendation.id)} className="cursor-pointer gap-1.5" data-reopen-focus={recommendation.id} />}>
                  <CheckCircle2Icon aria-hidden className="size-3.5" />{recommendation.kind === "control_amendment" ? "Amend" : "Resolve"}
                </HoverCardTrigger>
              </ActionHint>
          )}
          <ActionHint heading="Reject this suggestion" body={<p>Record why this change is not needed. You can attach supporting evidence; the decision stays in the history.</p>}>
            <HoverCardTrigger render={<Button type="button" size="default" variant={header ? "outline" : "ghost"} disabled={busy} onClick={() => setRejecting(recommendation.id)} className="cursor-pointer gap-1.5" />}>
              <XCircleIcon aria-hidden className="size-3.5" />Reject
            </HoverCardTrigger>
          </ActionHint>
        </>
      );
    }
    return (
      <Button type="button" size="default" variant={header ? "outline" : "ghost"} disabled={busy} onClick={() => setReopening(recommendation.id)} aria-busy={saving} className={header ? "cursor-pointer gap-1.5" : "min-w-24 cursor-pointer gap-1.5"}>
        <RotateCcwIcon aria-hidden className="size-3.5" /><DecisionLabel saving={saving} label="Reopen" />
      </Button>
    );
  };

  const renderReview = (recommendation: Recommendation) => {
    if (decisionOnly === "header") return <div aria-busy={isSaving(recommendation.id)} className="grid auto-cols-fr grid-flow-col gap-2">{actions(recommendation)}</div>;
    if (decisionOnly) {
      return <div aria-busy={isSaving(recommendation.id)} className="flex flex-col items-stretch justify-between gap-3 border-y border-border/60 py-4 sm:flex-row sm:flex-wrap sm:items-center">
        <p className="text-sm font-medium">{recommendation.status === "applied" ? "This suggestion was applied." : recommendation.status === "dismissed" ? "This suggestion was rejected." : recommendation.status === "accepted" ? "Marked in progress. Ready for a decision?" : "Ready for a decision?"}<ProofLink organizationId={organizationId} recommendation={recommendation} /></p>
        <div className="flex flex-wrap items-center gap-1.5">{actions(recommendation)}</div>
      </div>;
    }
    const pill = STATUS_PILL[recommendation.status];
    const proposal = proposalLine(recommendation);
    const wording = proposedWording(recommendation);
    return (
      <div aria-busy={isSaving(recommendation.id)} className="p-4">
        <ReviewCardLayout references={bare ? null : (
                <Link
                  to="/$org/compliance/suggestions/$recommendationId"
                  params={{ org: orgSegment, recommendationId: recommendation.id }}
                  search={policyId ? { fromPolicy: policyId } : {}}
                  className={REVIEW_REFERENCE_CLASS_NAME}
                >
                  {recommendation.source === "inspector" ? <InspectorAvatar className="size-5 shrink-0 rounded-sm [&_svg]:size-3" /> : <SparklesIcon aria-hidden className="size-4" />}
                  <span className={REVIEW_REFERENCE_TEXT_CLASS_NAME}><span className="mb-0.5 block text-[11px] font-medium text-muted-foreground">{recommendation.source === "inspector" ? "Inspector proposal" : "Proposal"}</span><span className="font-medium underline decoration-border underline-offset-4 group-hover/source:decoration-current">Review suggestion</span></span>
                  <ArrowRightIcon aria-hidden className="size-3.5 shrink-0" />
                </Link>
          )} actions={actions(recommendation)} >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className={cn(pill.className, "h-auto border-0 px-2 py-1 text-xs sm:h-auto")}>
                {pill.label}
              </Badge>
              {recommendation.severity ? <Badge variant="secondary" className={cn(findingSeverityTone(recommendation.severity).badge, "h-auto border-0 px-2 py-1 text-xs sm:h-auto")}>{FINDING_SEVERITY_LABELS[recommendation.severity] ?? recommendation.severity}</Badge> : null}
              <span className="text-xs text-muted-foreground">{KIND_LABELS[recommendation.kind]}</span>
              <time dateTime={recommendation.createdAt} className="ml-auto text-xs text-muted-foreground/75">{formatDate(recommendation.createdAt)}</time>
            </div>
            <p className="mt-2.5 text-[15px] font-semibold leading-6 tracking-tight text-foreground">{recommendation.title}</p>
            {bare ? <DecisionBrief summary={suggestionTldr(recommendation)} label="Read full rationale">
              <p className="whitespace-pre-line">{recommendation.rationale}</p>
              {proposal ? (
                <p className="mt-2 text-sm text-foreground">
                  <span className="text-muted-foreground">
                    {recommendation.kind === "control_amendment" ? "Where: " : "Change: "}
                  </span>
                  {proposal}
                </p>
              ) : null}
              {wording ? (
                <blockquote className="mt-2 whitespace-pre-line border-l-2 border-border ps-3 text-sm text-foreground">
                  {wording}
                </blockquote>
              ) : null}
            </DecisionBrief> : (
              <>
                <ReviewSummary summary={firstSentence(recommendation.rationale)} change={suggestionTldr(recommendation)} />
                {/*
                  Approve sits on this card and writes immediately, so a
                  company-profile change has to show the whole of what would be
                  written — approving from a summary is approving text nobody
                  read, and the text came from a run that read the
                  organisation's own documents. The proposal is capped at a
                  readable length precisely so it can be shown in place.
                */}
                {recommendation.kind === "org_context" && wording ? (
                  <blockquote className="mt-2 whitespace-pre-line border-l-2 border-border ps-3 text-sm text-foreground">
                    {wording}
                  </blockquote>
                ) : null}

              </>
            )}
            {recommendation.status === "applied" ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {recommendation.resolvedVersion != null
                  ? `Resolved in v${recommendation.resolvedVersion}`
                  : recommendation.resolvedPolicyName
                    ? `Resolved by ${recommendation.resolvedPolicyName.replace(/\.pdf$/i, "")}`
                    : "Applied"}
                {recommendation.decidedByEmail ? ` by ${recommendation.decidedByEmail}` : ""}
                {recommendation.decidedAt ? ` · ${formatDate(recommendation.decidedAt)}` : ""}
                {recommendation.decisionNote ? ` — ${recommendation.decisionNote}` : ""}
                <ProofLink organizationId={organizationId} recommendation={recommendation} />
              </p>
            ) : null}
            {recommendation.status === "dismissed" ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Rejected
                {recommendation.decidedByEmail ? ` by ${recommendation.decidedByEmail}` : ""}
                {recommendation.decisionNote ? ` — ${recommendation.decisionNote}` : ""}
                <ProofLink organizationId={organizationId} recommendation={recommendation} />
              </p>
            ) : null}
          </div>

        </ReviewCardLayout>
      </div>
    );
  };

  const render = (recommendation: Recommendation) => (
    <Card render={<li />} key={recommendation.id} id={`suggestion-${recommendation.id}`} data-suggestion-id={recommendation.id}
      className="min-w-0 border-border/60 target:ring-2 target:ring-primary/40">
      {renderReview(recommendation)}
    </Card>
  );

  const resolvingRecommendation = recommendations.find((item) => item.id === resolving) ?? null;
  const rejectingRecommendation = recommendations.find((item) => item.id === rejecting) ?? null;
  const reopeningRecommendation =
    recommendations.find((item) => item.id === reopening && !isOpenSuggestion(item)) ?? null;

  return (
    <section ref={sectionRef} className="space-y-3">
      <RejectDialog
        open={rejectingRecommendation !== null}
        onOpenChange={(open) => {
          if (!open) setRejecting(null);
        }}
        what="suggestion"
        title={rejectingRecommendation?.title ?? null}
        onReject={({ reason, proof }) =>
          rejectingRecommendation === null
            ? Promise.resolve("Nothing to reject.")
            : run(rejectingRecommendation.id, "dismiss", {
                note: reason,
                ...(proof === undefined ? {} : { proof }),
              })
        }
      />
      <ReopenDialog
        open={reopeningRecommendation !== null}
        onOpenChange={(open) => {
          if (!open) setReopening(null);
        }}
        title={reopeningRecommendation?.title ?? null}
        summary={reopeningRecommendation === null ? null : suggestionReopenSummary(reopeningRecommendation)}
        onReopen={() =>
          reopeningRecommendation === null
            ? Promise.resolve("Nothing to reopen.")
            : run(reopeningRecommendation.id, "reopen").then((error) => {
                if (error === null) setFocusAfterReopen(reopeningRecommendation.id);
                return error;
              })
        }
      />
      <ResolveRecommendationDialog
        organizationId={organizationId}
        recommendation={resolvingRecommendation}
        open={resolvingRecommendation !== null}
        onOpenChange={(open) => {
          if (!open) setResolving(null);
        }}
        onResolve={(evidence) => resolvingRecommendation === null
          ? Promise.resolve("Nothing to resolve.")
          : run(resolvingRecommendation.id, "apply", {
              ...(evidence.note === null ? {} : { note: evidence.note }),
              ...(evidence.resolvedVersion === undefined ? {} : { resolvedVersion: evidence.resolvedVersion }),
              ...(evidence.resolvedPolicyId === undefined ? {} : { resolvedPolicyId: evidence.resolvedPolicyId }),
            })}
      />
      {bare || compact || decisionOnly ? null : (
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <LightbulbIcon className="size-4 text-muted-foreground" />
          {heading}
          <span className="text-xs font-normal tabular-nums text-muted-foreground">
            {open.length}
          </span>
        </h2>
      )}
      {feedback}
      {decisionOnly ? recommendations.map((item) => <div key={item.id}>{renderReview(item)}</div>) : compact ? (
        <SuggestionQueue key={JSON.stringify([organizationId, filters, queueSort])} recommendations={recommendations}
          orgSegment={orgSegment} sort={queueSort} {...(documentLabels === undefined ? {} : { documentLabels })} />
      ) : <>
      {open.length > 0 ? (
        everything && !bare ? (
          <div className="space-y-6">
            {groupByDocument(open).map((group) => (
              <section key={group.key} className="space-y-3">
                <h3 className="flex items-baseline gap-2 text-sm font-semibold text-foreground">
                  {group.policyId === null ? (
                    group.policyName
                  ) : (
                    <Link
                      to="/$org/compliance/policies/$policyId"
                      params={{ org: orgSegment, policyId: group.policyId }}
                      className="hover:underline"
                    >
                      {group.policyName}
                    </Link>
                  )}
                  <span className="text-xs font-normal tabular-nums text-muted-foreground">
                    {group.items.length}
                  </span>
                </h3>
                <ul className="space-y-3">{group.items.map(render)}</ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="space-y-3">{open.map(render)}</ul>
        )
      ) : null}
      {decided.length > 0 ? (
        <details open={bare || open.length === 0 || undefined} className="group rounded-xl border border-border/60 bg-card px-4 py-3 [&_summary::-webkit-details-marker]:hidden">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-foreground">
            <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
            Decided
            <span className="text-xs font-normal tabular-nums text-muted-foreground">
              {decided.length}
            </span>
          </summary>
          <ul className="mt-3 space-y-3">{decided.map(render)}</ul>
        </details>
      ) : null}
      </>}
    </section>
  );
}
